import {
    ActionContext,
    ActionTree,
    GetterTree,
    MutationTree,
    Module,
    Store as VuexStore,
    CommitOptions,
    DispatchOptions,
} from 'vuex'

import { State as RootState } from '@/store'

import {Alert} from "@/types/AlertType";
import {AlertType} from "@/types";

// Declare state
export type State = {
    alerts: Alert[],
}

// Mutation contracts
export type Mutations<S = State> = {
    add(state: S, { alert }: { alert: Alert }): void,
    remove(state: S, { id }: { id: string }): void
}

// Actions context
type AugmentedActionContext = {
    commit<K extends keyof Mutations>(
        key: K,
        payload: Parameters<Mutations[K]>[1],
    ): ReturnType<Mutations[K]>
} & Omit<ActionContext<State, RootState>, 'commit'>

// Actions contracts
export interface Actions {
    add(
        { commit }: AugmentedActionContext,
        payload: { alert: Alert }
    ): void
    remove(
        { commit }: AugmentedActionContext,
        payload: { id: string },
    ): void,
    success(
        { commit }: AugmentedActionContext,
        payload: { message: string, timeout: number, callback: () => void },
    ): void,
    error(
        { commit }: AugmentedActionContext,
        payload: { message: string, timeout: number, callback: () => void },
    ): void,
    warning(
        { commit }: AugmentedActionContext,
        payload: { message: string, timeout: number, callback: () => void },
    ): void,
    info(
        { commit }: AugmentedActionContext,
        payload: { message: string, timeout: number, callback: () => void },
    ): void
}

// Getters types
export type Getters = {
    alerts(state: State): Alert[]
}

const state: State = {
    alerts: []
};

const getters: GetterTree<State, RootState> & Getters = {
    alerts: (state) => {
        return state.alerts;
    },
};

const mutations: MutationTree<State> & Mutations = {
    add(state, { alert }) {
        state.alerts.push(alert);
    },
    remove(state, { id }) {
        state.alerts = state.alerts.filter((alert) => {
            return alert.id != id;
        })
    }
};

const actions: ActionTree<State, RootState> & Actions = {
    add ({commit}, {alert}) {
        commit('add', {alert});

        if(alert.timeout != 0) {
            setTimeout(() => {
                commit('remove', {id: alert.id});

                alert.callback();
            }, alert.timeout * 1000);
        }
    },

    remove({commit}, {id}) {
        commit('remove', {id});
    },

    success({commit, dispatch}, {message, timeout, callback} = {message: "Success", timeout: 5, callback: () => {}}) {
        dispatch('add', {
            alert: {
                id: Math.random().toString(36),
                type: AlertType.success,
                message: message,
                timeout: timeout !== undefined ? timeout : 5,
                callback: callback !== undefined ? callback : () => {}
            }
        });
    },

    error({commit, dispatch}, {message, timeout, callback} = {message: "Error", timeout: 10, callback: () => {}}) {
        dispatch('add', {
            alert: {
                id: Math.random().toString(36),
                type: AlertType.error,
                message: message,
                timeout: timeout !== undefined ? timeout : 10,
                callback: callback !== undefined ? callback : () => {}
            }
        });
    },

    warning({commit, dispatch}, {message, timeout, callback} = {message: "Warning", timeout: 10, callback: () => {}}) {
        dispatch('add', {
            alert: {
                id: Math.random().toString(36),
                type: AlertType.warning,
                message: message,
                timeout: timeout !== undefined ? timeout : 10,
                callback: callback !== undefined ? callback : () => {}
            }
        });
    },

    info({commit, dispatch}, {message, timeout, callback} = {message: "Info", timeout: 8, callback: () => {}}) {
        dispatch('add', {
            alert: {
                id: Math.random().toString(36),
                type: AlertType.info,
                message: message,
                timeout: timeout !== undefined ? timeout : 8,
                callback: callback !== undefined ? callback : () => {}
            }
        });
    }
};

//setup store type
export type Store<S = State> = Omit<
    VuexStore<S>,
    'commit' | 'getters' | 'dispatch'
    > & {
    commit<K extends keyof Mutations, P extends Parameters<Mutations[K]>[1]>(
        key: K,
        payload: P,
        options?: CommitOptions,
    ): ReturnType<Mutations[K]>
} & {
    getters: {
        [K in keyof Getters]: ReturnType<Getters[K]>
    }
} & {
    dispatch<K extends keyof Actions>(
        key: K,
        payload: Parameters<Actions[K]>[1],
        options?: DispatchOptions,
    ): ReturnType<Actions[K]>
}

export const AlertsModule: Module<State, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}